import { getStoryblokApi } from "@storyblok/react/rsc";

import { storyblokCache } from "./storyblok-cache";

export type FaqTopic = { value: string; name: string };

// Fallback for the "faq_topics" datasource, used only when the API is
// unavailable. The live list comes from fetchFaqTopics.
export const FAQ_TOPICS: readonly FaqTopic[] = [
  { value: "donor-sperm", name: "Donor Sperm" },
  { value: "clinic-questions", name: "Clinic Questions" },
  { value: "funding-and-pricing", name: "Funding and Pricing" },
  { value: "patient-portal", name: "Patient Portal" },
  { value: "ivf", name: "IVF" },
  { value: "icsi", name: "ICSI" },
  { value: "frozen-embryo-transfer", name: "Frozen Embryo Transfer" },
  { value: "egg-freezing", name: "Egg Freezing" },
  { value: "transvaginal-ultrasounds", name: "Transvaginal Ultrasounds" },
  { value: "blood-tests", name: "Blood Tests" },
  { value: "fertility-tests-for-men", name: "Fertility Tests for Men" },
  { value: "semen-analysis", name: "Semen Analysis" },
  { value: "sperm-dna-fragmentation", name: "Sperm DNA Fragmentation" },
  { value: "surgical-sperm-retrieval", name: "Surgical Sperm Retrieval" },
  { value: "reciprocal-ivf", name: "Reciprocal IVF" },
  { value: "iui", name: "IUI" },
  { value: "fertility-tests-for-women", name: "Fertility Tests for Women" },
  {
    value: "travelling-for-fertility-treatments",
    name: "Travelling for Fertility Treatments",
  },
];

const TOPIC_BY_VALUE = new Map<string, FaqTopic>(
  FAQ_TOPICS.map((t) => [t.value, t]),
);

export function findTopic(value: string | undefined): FaqTopic | undefined {
  if (!value) return undefined;
  return TOPIC_BY_VALUE.get(value);
}

export function humanizeTopic(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function topicLabel(value: string | undefined): string {
  if (!value) return "";
  return findTopic(value)?.name ?? humanizeTopic(value);
}

export function resolveTopicName(value: string, topics: FaqTopic[]): string {
  return (
    topics.find((t) => t.value === value)?.name ??
    findTopic(value)?.name ??
    humanizeTopic(value)
  );
}

type DatasourceEntry = { name?: string; value?: string };

// Falls back to FAQ_TOPICS so a transient API failure can't blank the lists.
export async function fetchFaqTopics(isDraft = false): Promise<FaqTopic[]> {
  try {
    const { data } = await getStoryblokApi().get(
      "cdn/datasource_entries",
      { datasource: "faq_topics", per_page: 1000 },
      storyblokCache(isDraft, ["faqs"]),
    );
    const entries = (data?.datasource_entries ?? []) as DatasourceEntry[];
    const topics = entries
      .filter((e): e is FaqTopic => Boolean(e.value && e.name))
      .map((e) => ({ value: e.value, name: e.name }));
    return topics.length > 0 ? topics : [...FAQ_TOPICS];
  } catch {
    return [...FAQ_TOPICS];
  }
}
